import { DebounceOverrideInterface } from "../../../interfaces/DebounceOverride.interface";
import { formatUuidT } from "../../../flatbuffers/display";
import { LN2_MESSAGE_CATEGORIES } from "../../../flatbuffers/types/ln2MessageOptions";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { Traffic } from "../../../flatbuffers/generated/traffic/traffic";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { Service } from "../../../flatbuffers/generated/service/service";
import { Control } from "../../../flatbuffers/generated/control/control";
import { Fault } from "../../../flatbuffers/generated/fault/fault";
import { Status } from "../../../flatbuffers/generated/status/status";
import { GroupSupervisoryStatus } from "../../../flatbuffers/generated/group-supervisory-status/group-supervisory-status";
import { GroupControl } from "../../../flatbuffers/generated/group-control/group-control";

export interface OverrideRow {
  key: string;
  keyId: string;
  shortMessage: string;
  deviceType: string;
  enumId: number | null;
  encodedType: number;
  messageContent: number;
  messageValue: number;
  adapterDefault: number | null;
  bankOverrideTime: number | null;
  overrideTime: number | null;
  existingOverrideId: string | null;
}

export interface SaveAction {
  type: "POST" | "PUT" | "DELETE";
  keyId: string;
  encodedType: number;
  messageContent: number;
  messageValue: number;
  overrideId?: string | null;
  debounceTime?: number | null;
}

/**
 * Resolve the enum value name from the FlatBuffer generated enums.
 */
function resolveEnumName(mc: number, mv: number): string {
  switch (mc as MessageContent) {
    case MessageContent.Traffic_TrafficMessage:
      return Traffic[mv] ?? `Traffic(${mv})`;
    case MessageContent.Usage_UsageMessage:
      return Usage[mv] ?? `Usage(${mv})`;
    case MessageContent.Service_ServiceMessage:
      return Service[mv] ?? `Service(${mv})`;
    case MessageContent.Control_ControlMessage:
      return Control[mv] ?? `Control(${mv})`;
    case MessageContent.Fault_FaultMessage:
      return Fault[mv] ?? `Fault(${mv})`;
    case MessageContent.Status_StatusMessage:
      return Status[mv] ?? `Status(${mv})`;
    case MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage:
      return GroupSupervisoryStatus[mv] ?? `GSS(${mv})`;
    case MessageContent.GroupControl_GroupControlMessage:
      return GroupControl[mv] ?? `GC(${mv})`;
    default:
      return `${mc}:${mv}`;
  }
}

function resolveCategoryLabel(mc: number): string {
  const category = LN2_MESSAGE_CATEGORIES.find((c) => c.messageContent === mc);
  return category ? category.label : `Type(${mc})`;
}

/**
 * Build table rows from adapter keys and scoped overrides.
 * Uses FlatBuffer-based fields on the key directly — no Message relation needed.
 * Pass bankOverrides when building lift-scoped rows to populate bankOverrideTime.
 */
export function buildOverrideRows(
  adapterKeys: any[],
  scopedOverrides: DebounceOverrideInterface[],
  bankOverrides?: DebounceOverrideInterface[],
): OverrideRow[] {
  return adapterKeys.map((k: any) => {
    const mc: number = k.messageContent ?? 0;
    const mv: number = k.messageValue ?? 0;
    const et: number = k.encodedType ?? 1;
    const categoryLabel = resolveCategoryLabel(mc);
    const enumName = resolveEnumName(mc, mv);
    // Match override by FlatBuffer fields (not message_id UUID)
    const existing = scopedOverrides.find(
      (o) => o.encodedType === et && o.messageContent === mc && o.messageValue === mv,
    );
    const bankExisting = bankOverrides?.find(
      (o) => o.encodedType === et && o.messageContent === mc && o.messageValue === mv,
    );
    const keyId =
      typeof k.id === "object" && k.id !== null
        ? formatUuidT(k.id) || `${et}:${mc}:${mv}`
        : k.id || `${et}:${mc}:${mv}`;
    const existingId = existing ? normalizeId(existing.id) : null;
    return {
      key: keyId,
      keyId,
      shortMessage: `${categoryLabel}::${enumName}`,
      deviceType: k.fbsDeviceType ?? "",
      enumId: mv,
      encodedType: et,
      messageContent: mc,
      messageValue: mv,
      adapterDefault: k.debounceTime ?? 0,
      bankOverrideTime: bankExisting ? bankExisting.debounceTime : null,
      overrideTime: existing ? existing.debounceTime : null,
      existingOverrideId: existingId,
    };
  });
}

/**
 * Compute the diff of save actions needed to reconcile table state with existing overrides.
 */
export function computeSaveActions(
  tableData: OverrideRow[],
  scopedOverrides: DebounceOverrideInterface[],
): SaveAction[] {
  const actions: SaveAction[] = [];

  tableData.forEach((row) => {
    const hasValue = row.overrideTime !== null && row.overrideTime > 0;
    const hadExisting = row.existingOverrideId !== null;

    if (hasValue && !hadExisting) {
      actions.push({
        type: "POST",
        keyId: row.keyId,
        encodedType: row.encodedType,
        messageContent: row.messageContent,
        messageValue: row.messageValue,
        debounceTime: row.overrideTime,
      });
    } else if (hasValue && hadExisting) {
      const existing = scopedOverrides.find((o) => normalizeId(o.id) === row.existingOverrideId);
      if (existing && existing.debounceTime !== row.overrideTime) {
        actions.push({
          type: "PUT",
          keyId: row.keyId,
          encodedType: row.encodedType,
          messageContent: row.messageContent,
          messageValue: row.messageValue,
          overrideId: row.existingOverrideId,
          debounceTime: row.overrideTime,
        });
      }
    } else if (!hasValue && hadExisting) {
      actions.push({
        type: "DELETE",
        keyId: row.keyId,
        encodedType: row.encodedType,
        messageContent: row.messageContent,
        messageValue: row.messageValue,
        overrideId: row.existingOverrideId,
      });
    }
  });

  return actions;
}

/** Normalize an id field that may be a UUID object, string, or null. */
function normalizeId(raw: any): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "object") return formatUuidT(raw) || null;
  return raw;
}

/**
 * Filter overrides by scope (bank-wide when liftId is null, or specific lift).
 * Handles UUID-object liftId values from FlatBuffer-decoded responses.
 */
export function filterOverridesByScope(
  allOverrides: DebounceOverrideInterface[],
  selectedLiftId: string | null,
): DebounceOverrideInterface[] {
  return allOverrides.filter((o) => {
    const liftId = normalizeId(o.liftId);
    return selectedLiftId === null ? liftId === null : liftId === selectedLiftId;
  });
}
